import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { jwtDecode } from "jwt-decode";
import { DthTokenCrypto, DthTokenStore, type IdbEncryptedRecord } from "./DthTokenCrypto";

const logger = baseLogger.getChild("DthValidationTokenManager");

/**
 * Types of validation tokens the client may store.
 * - LOGIN:    token issued during email/password login flow
 * - AUTH_APP: token issued for authenticator-app related flows
 */
export enum DthValidationTokenType {
    LOGIN = "LOGIN",
    AUTH_APP = "AUTH_APP",
}

/**
 * Decrypted payload for a single validation token.
 * - `token`:     the raw token string (commonly a JWT)
 * - `userId`:    id of the user who owns the token
 * - `type`:      the token type
 * - `timestamp`: save time in milliseconds since epoch
 * - `extra`:     optional field for future extensions
 */
export interface DthStoredValidationToken {
    token: string;
    userId: string;
    type: DthValidationTokenType;
    timestamp: number;
    extra: Record<string, any> | null;
}

/**
 * Secure, async manager for validation tokens.
 *
 * Tokens are stored in **IndexedDB** as AES-GCM-256 ciphertexts with a
 * HMAC-SHA-256 integrity tag.  The encryption keys are non-extractable
 * `CryptoKey` objects persisted in IDB — JavaScript code can never read their
 * raw bytes, even under XSS.
 *
 * Because tokens live in IndexedDB (not localStorage) they:
 *   • survive page refreshes and application restarts, up to their expiry date;
 *   • are NOT wiped by `localStorage.clear()`;
 *   • are automatically invalidated when their `expiresAt` timestamp passes.
 *
 * All public methods are async — callers must `await` them.
 */
export default class DthValidationTokenManager {
    private static readonly TOKEN_STORE = DthTokenStore.Tokens;

    /**
     * Fallback TTL (milliseconds) used when the JWT carries no `exp` claim.
     */
    public static EXPIRATION_MS: Record<DthValidationTokenType, number> = {
        [DthValidationTokenType.LOGIN]: 5 * 60 * 1000,
        [DthValidationTokenType.AUTH_APP]: 5 * 60 * 1000,
    };

    // ── Helpers ───────────────────────────────────────────────────────────────

    private static recordId(userId: string, type: DthValidationTokenType): string {
        return `${userId}:${type}`;
    }

    private static resolveExpiresAt(token: string, type: DthValidationTokenType): number {
        const exp = this.getJwtExpirationByTokenInSeconds(token);
        return exp ?? Math.floor((Date.now() + this.EXPIRATION_MS[type]) / 1000);
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Encrypt and persist a validation token for the given user and type.
     * Overwrites any existing token for the same (userId, type) pair.
     *
     * @param token           Raw token string (usually a JWT).
     * @param userId          Id of the owning user.
     * @param type            Token type.
     * @param resetOtherUsers When true, tokens for all other users are removed.
     * @param extra           Optional metadata stored alongside the token.
     */
    public static async save(
        token: string,
        userId: string,
        type: DthValidationTokenType,
        resetOtherUsers = true,
        extra: Record<string, any> | null = null,
    ): Promise<void> {
        if (!token || !userId) return;

        const payload: DthStoredValidationToken = {
            token,
            userId,
            type,
            timestamp: Date.now(),
            extra,
        };
        const expiresAt = this.resolveExpiresAt(token, type);
        const id = this.recordId(userId, type);

        try {
            const record = await DthTokenCrypto.encrypt(JSON.stringify(payload), id, expiresAt);
            await DthTokenCrypto.saveRecord(record, DthValidationTokenManager.TOKEN_STORE);
        } catch (e) {
            logger.warn("save: failed to encrypt/persist token", e);
            return;
        }

        if (resetOtherUsers) await this.removeOtherUsers(userId);
    }

    /**
     * Retrieve the raw token string for a (userId, type) pair.
     * Returns null when the token is absent, expired, or fails integrity checks.
     */
    public static async get(userId: string, type: DthValidationTokenType): Promise<string | null> {
        const stored = await this.getStored(userId, type);
        return stored?.token ?? null;
    }

    /**
     * Retrieve the full stored payload for a (userId, type) pair.
     * Returns null when the token is absent, expired, or fails integrity checks.
     *
     * Integrity is verified at two levels:
     *   1. HMAC over the IDB record (prevents storage tampering).
     *   2. JWT `exp` claim cross-check (prevents `expiresAt` field inflation).
     */
    public static async getStored(
        userId: string,
        type: DthValidationTokenType,
    ): Promise<DthStoredValidationToken | null> {
        if (!userId) return null;
        const id = this.recordId(userId, type);

        let record: IdbEncryptedRecord | null;
        try {
            record = await DthTokenCrypto.loadRecord(id, DthValidationTokenManager.TOKEN_STORE);
        } catch (e) {
            logger.warn("getStored: IDB load failed", e);
            return null;
        }

        if (!record) return null;

        // Fast path: plaintext expiry check avoids unnecessary decryption work.
        if (record.expiresAt <= Math.floor(Date.now() / 1000)) {
            void DthTokenCrypto.deleteRecord(id, DthValidationTokenManager.TOKEN_STORE).catch(() => {});
            return null;
        }

        // Verify HMAC and decrypt.
        const plaintext = await DthTokenCrypto.decrypt(record).catch(() => null);
        if (!plaintext) {
            logger.warn("getStored: integrity/decryption failure — discarding record", id);
            void DthTokenCrypto.deleteRecord(id, DthValidationTokenManager.TOKEN_STORE).catch(() => {});
            return null;
        }

        let stored: DthStoredValidationToken;
        try {
            stored = JSON.parse(plaintext) as DthStoredValidationToken;
        } catch {
            logger.warn("getStored: JSON parse failure — discarding record", id);
            void DthTokenCrypto.deleteRecord(id, DthValidationTokenManager.TOKEN_STORE).catch(() => {});
            return null;
        }

        // Secondary expiry check using the JWT `exp` claim as a tamper-resistant source.
        // This guards against an attacker inflating the plaintext `expiresAt` field.
        if (this.isExpired(stored)) {
            void DthTokenCrypto.deleteRecord(id, DthValidationTokenManager.TOKEN_STORE).catch(() => {});
            return null;
        }

        return stored;
    }

    /**
     * Remove the token stored for a specific (userId, type) pair.
     */
    public static async remove(userId: string, type: DthValidationTokenType): Promise<void> {
        await DthTokenCrypto.deleteRecord(this.recordId(userId, type), DthValidationTokenManager.TOKEN_STORE).catch((e) =>
            logger.warn("remove: IDB delete failed", e),
        );
    }

    /**
     * Remove all stored tokens of a given type across all users.
     */
    public static async resetType(type: DthValidationTokenType): Promise<void> {
        const suffix = `:${type}`;
        const ids = await DthTokenCrypto.getAllIds(DthValidationTokenManager.TOKEN_STORE).catch(() => [] as string[]);
        await Promise.all(ids.filter((id) => id.endsWith(suffix)).map((id) => DthTokenCrypto.deleteRecord(id, DthValidationTokenManager.TOKEN_STORE)));
    }

    /**
     * Remove all stored validation tokens.
     */
    public static async resetAll(): Promise<void> {
        await DthTokenCrypto.clearAll(DthValidationTokenManager.TOKEN_STORE).catch((e) => logger.warn("resetAll: failed", e));
    }

    /**
     * Handle a change of the currently authenticated user.
     *
     * Clears all tokens when `currentUserId` is undefined; otherwise removes
     * tokens belonging to other users.
     *
     * @returns true when at least one token was removed.
     */
    public static async onUserChanged(currentUserId?: string): Promise<boolean> {
        if (!currentUserId) {
            await this.resetAll();
            return true;
        }
        return this.removeOtherUsers(currentUserId);
    }

    /**
     * Decode the JWT and return the `exp` claim (Unix seconds) if present.
     * Performs header+payload decode only — does NOT verify the signature.
     */
    public static getJwtExpirationByTokenInSeconds(token: string): number | null {
        try {
            const decoded = jwtDecode<{ exp?: number }>(token);
            return typeof decoded?.exp === "number" ? decoded.exp : null;
        } catch {
            return null;
        }
    }

    /**
     * Get the expiration Unix-second timestamp for the stored token of a user/type.
     * Returns null if the token is absent or invalid.
     */
    public static async getJwtExpirationInSeconds(
        userId: string,
        type: DthValidationTokenType,
    ): Promise<number | null> {
        const stored = await this.getStored(userId, type);
        return stored ? this.getJwtExpirationByTokenInSeconds(stored.token) : null;
    }

    /**
     * Delete all expired token records from IndexedDB.
     * Uses the `expiresAt` index — no decryption required, very fast.
     */
    public static async refresh(): Promise<void> {
        try {
            const count = await DthTokenCrypto.deleteExpired(DthValidationTokenManager.TOKEN_STORE);
            if (count > 0) logger.debug(`refresh: removed ${count} expired token(s)`);
        } catch (e) {
            logger.warn("refresh: cleanup failed", e);
        }
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private static async removeOtherUsers(currentUserId: string): Promise<boolean> {
        const prefix = `${currentUserId}:`;
        const ids = await DthTokenCrypto.getAllIds(DthValidationTokenManager.TOKEN_STORE).catch(() => [] as string[]);
        const others = ids.filter((id) => !id.startsWith(prefix));
        if (others.length === 0) return false;
        await Promise.all(others.map((id) => DthTokenCrypto.deleteRecord(id, DthValidationTokenManager.TOKEN_STORE)));
        return true;
    }

    private static isExpired(stored: DthStoredValidationToken): boolean {
        const expSec = this.getJwtExpirationByTokenInSeconds(stored.token);
        if (expSec !== null) return expSec <= Math.floor(Date.now() / 1000);
        return Date.now() >= stored.timestamp + this.EXPIRATION_MS[stored.type];
    }
}

// ── Module-load side effects ───────────────────────────────────────────────────

// Fire-and-forget: remove any tokens that already expired while the app was closed.
void DthValidationTokenManager.refresh();
